import { useState, useEffect } from "react";
import {
  getInvitedEmployees,
  createEmployeeInvite,
  removeEmployee,
} from "../../services/operations/adminAPI";
import toast from "react-hot-toast";
import { IoMdPersonAdd } from "react-icons/io";
import { MdPersonRemoveAlt1 } from "react-icons/md";
import { motion, AnimatePresence } from 'framer-motion'; // Import for modal animations

const MyTeamPage = () => {
  const [employees, setEmployees] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [newEmployee, setNewEmployee] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [message, setMessage] = useState(null); // For potential message display

  // Fetch employees
  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await getInvitedEmployees();
      setEmployees(response.data);
    } catch (error) {
      console.error("Error fetching employees:", error);
      setMessage({ type: 'error', text: 'Failed to fetch employees.' });
    }
  };

  // Handle adding a new employee
  const handleAddEmployee = async () => {
    if ( !newEmployee.email) {
      toast.error("Please fill all fields!");
      return;
    }

    try {
      const res = await createEmployeeInvite(newEmployee);
      if (!res) toast.loading("Loading...");
      toast.success("Invitation sent!");
      setShowAddModal(false);
      setNewEmployee({ firstName: "", lastName: "", email: "" });
      fetchEmployees();
      setMessage({ type: 'success', text: 'Invitation sent successfully!' });
    } catch (error) {
      toast.error("Failed to send invitation");
      setMessage({ type: 'error', text: `Failed to send invitation: ${error.message}` });
    }
  };

  // Handle remove employee
  const handleRemoveClick = (employee) => {
    setSelectedEmployee(employee);
    setShowConfirmModal(true);
  };

  const confirmRemoveEmployee = async () => {
    if (!selectedEmployee) return;

    try {
      await removeEmployee(selectedEmployee._id);
      toast.success("Employee removed!");
      setEmployees(employees.filter((e) => e._id !== selectedEmployee._id));
      setShowConfirmModal(false);
      setMessage({ type: 'success', text: 'Employee removed successfully!' });
    } catch (error) {
      toast.error("Failed to remove employee");
      setMessage({ type: 'error', text: 'Failed to remove employee.' });
    }
  };

  const cn = (...classes) => {
    return classes.filter(Boolean).join(' ');
  };

  return (
    <div className="">
      <h1 className="text-3xl font-bold mb-6 flex items-center gap-2 text-blue-500">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-user-plus w-8 h-8"
        >
          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
          <circle cx="8" cy="7" r="4"></circle>
          <path d="M19 8v6m-3-3h6"></path>
        </svg>
        My Team
      </h1>

      <div className="flex justify-end mb-4">
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-gradient-to-r from-blue-500 to-blue-700 text-white font-bold py-2 px-4 rounded flex items-center gap-2"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-plus w-4 h-4"
          >
            <path d="M5 12h14"></path>
            <path d="M12 5v14"></path>
          </svg>
          Add Employee
        </button>
      </div>

      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black bg-opacity-50 p-2 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.8, y: -20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 20 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md"
            >
              <h2 className="text-2xl font-semibold mb-4 text-blue-500">Add New Employee</h2>
              <div className="space-y-4">
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={newEmployee.email}
                    onChange={(e) => setNewEmployee({ ...newEmployee, email: e.target.value })}
                    className="mt-1 block py-2 w-full rounded-md border border-blue-300 shadow-sm  focus:outline-none focus:ring-blue-500 sm:text-sm"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddEmployee}
                  className="ml-2 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center gap-2"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-mail w-4 h-4"
                  >
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                  Send Invitation
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showConfirmModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 p-2 bg-black bg-opacity-50 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.8, y: -20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 20 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md"
            >
              <h3 className="text-lg font-semibold text-red-500 mb-4">
                Confirm Remove?
              </h3>
              <p className="text-gray-700 mb-4">
                Are you sure you want to remove{" "}
                <span className="font-semibold">
                  {selectedEmployee?.firstName} {selectedEmployee?.lastName}
                </span>
                ?
              </p>
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded"
                >
                  No
                </button>
                <button
                  onClick={confirmRemoveEmployee}
                  className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
                >
                  Yes, Remove
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {employees.length > 0 ? (
        <div className="bg-white rounded-lg shadow-md p-6 mt-6">
          <h2 className="text-xl font-semibold mb-4">Employee List</h2>
          <table className="w-full table-auto">
            <thead>
              <tr className="text-center text-gray-600">
                <th className="pb-2">S.No</th>
                <th className="pb-2">Name</th>
                <th className="pb-2">Email</th>
                <th className="pb-2 ">Actions</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((emp) => (
                <tr key={emp._id} className="border-t text-center items-center">
                  <td className="py-2">{employees.indexOf(emp) + 1}</td>
                  <td className="py-2">{emp.firstName} {emp.lastName}</td>
                  <td className="py-2">{emp.email}</td>
                  <td className="py-2 px-2 flex justify-center">
                    <button
                      onClick={() => handleRemoveClick(emp)}
                      className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-2  rounded flex items-center gap-2 shadow-sm"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-trash-2 w-4 h-4"
                      >
                        <path d="M3 6h18"></path>
                        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                        <line x1="10" x2="10" y1="11" y2="17"></line>
                        <line x1="14" x2="14" y1="11" y2="17"></line>
                      </svg>
                      
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md p-6 mt-6 text-center">
          <p className="text-gray-500">No employees have been added yet.</p>
        </div>
      )}

      {/* Message Display */}
      <AnimatePresence>
        {message && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className={cn(
              'fixed top-4 left-1/2 transform -translate-x-1/2 rounded-md px-4 py-2 shadow-lg z-50',
              message.type === 'success'
                ? 'bg-green-100 text-green-800 border border-green-300'
                : 'bg-red-100 text-red-800 border border-red-300'
            )}
            role={message.type === 'error' ? 'alert' : 'status'}
          >
            <div className="flex items-center gap-2">
              {message.type === 'error' ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-alert-triangle w-5 h-5"
                >
                  <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3z"></path>
                  <path d="M12 9v4"></path>
                  <path d="M12 17h.01"></path>
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-check-circle-2 w-5 h-5"
                >
                  <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path>
                  <path d="m9 12 2 2 4-4"></path>
                </svg>
              )}
              {message.text}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MyTeamPage;